import { useEffect, useMemo, useRef } from "react";
import SectionPicker from "./SectionPicker";
import DatePicker from "../../components/DatePicker";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./StudentProfileForm.module.css";

/**
 * Profile-style student form used by every + New / Edit entry point
 * (DirectoryPage generic modal, StudentsBySectionPanel inline quick-add,
 * and the standalone "New student" modal).
 *
 * The photo is a two-phase upload: while the form is open we hold the
 * picked File in `photoFile` and show a local preview. After the caller
 * saves the student (POST/PUT), they hit `POST /api/students/{id}/photo`
 * with the File to actually persist it. This lets a create flow pick a
 * photo before the student row exists.
 *
 * Props:
 *   value              student-shaped object (source of truth)
 *   onChange(next)     full-object patch
 *   sections, gradeLevels   for the inline SectionPicker
 *   photoFile          File | null — pending upload
 *   onPhotoFileChange  fn(File|null)
 *   showSectionPicker  bool (default true)
 *   showStatus         bool (default false — only shown on edit)
 *   disabled           bool
 *   contextHeader      optional ReactNode rendered above the layout
 *                      (used by the section-modal inline flow to say
 *                      "Adding to Grade 1 · St. Mary").
 */
export default function StudentProfileForm({
  value, onChange,
  sections = [], gradeLevels = [],
  photoFile, onPhotoFileChange,
  showSectionPicker = true,
  showStatus = false,
  disabled = false,
  contextHeader = null,
  // Create-mode only: when these are passed, the form renders a
  // "Student # prefix" control above Student #. The parent owns the state
  // (so it can persist to localStorage and trigger a next-number refetch
  // with debouncing). When `prefixes` is also supplied, the control is a
  // dropdown of configured prefixes (managed in Settings → Academic); when
  // omitted it falls back to a free-text input.
  prefix, onPrefixChange, prefixes,
}) {
  const fileInputRef = useRef(null);

  // Local object URL for the pending File preview. Revoked on change so we
  // don't leak blobs between renders.
  const pendingPreview = useMemo(() => {
    if (!photoFile) return null;
    return URL.createObjectURL(photoFile);
  }, [photoFile]);
  useEffect(() => {
    return () => { if (pendingPreview) URL.revokeObjectURL(pendingPreview); };
  }, [pendingPreview]);

  const previewUrl = pendingPreview || value?.photoUrl || null;

  function update(patch) { onChange({ ...value, ...patch }); }

  function handleFilePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please pick an image file (JPG, PNG, …).");
      e.target.value = "";
      return;
    }
    onPhotoFileChange?.(file);
  }

  function handleRemove() {
    onPhotoFileChange?.(null);
    update({ photoUrl: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div className={s.profileForm}>
      {contextHeader && <div className={s.contextHeader}>{contextHeader}</div>}

      <div className={s.profileLayout}>
        <div className={s.photoColumn}>
          <div className={s.photoFrame}>
            {previewUrl ? (
              <img src={previewUrl} alt="Student" />
            ) : (
              <div className={s.photoPlaceholder}>
                <span className={s.photoIcon}>📷</span>
                <span className={s.photoHint}>No photo</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFilePick}
          />
          <div className={s.photoActions}>
            <button type="button" className={s.btnGhost}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}>
              {previewUrl ? "Change photo" : "Upload photo"}
            </button>
            {previewUrl && (
              <button type="button" className={s.btnRemove}
                      onClick={handleRemove} disabled={disabled}>
                Remove
              </button>
            )}
          </div>
          <div className={s.photoHelp}>
            {photoFile
              ? <span className={s.photoPending}>Will upload after save · {(photoFile.size / 1024).toFixed(0)} KB</span>
              : value?.photoUrl
                ? <span>Saved photo</span>
                : <span>Optional · JPG, PNG up to 10 MB</span>}
          </div>
        </div>

        <div className={s.fieldsColumn}>
          {onPrefixChange !== undefined && (
            <div className={s.row}>
              <Field label="Student # prefix">
                {Array.isArray(prefixes) && prefixes.length > 0 ? (
                  <select className={s.input}
                          value={prefixes.includes(prefix) ? prefix : prefixes[0]}
                          onChange={e => onPrefixChange(e.target.value)}
                          disabled={disabled}>
                    {prefixes.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <input className={s.input} type="text"
                         value={prefix ?? ""}
                         onChange={e => onPrefixChange(e.target.value)}
                         placeholder="S-2026-"
                         disabled={disabled} />
                )}
                <div className={s.prefixHint}>
                  Manage prefixes in Settings → Academic. The next number auto-increments.
                </div>
              </Field>
            </div>
          )}
          <div className={s.row}>
            <Field label="Student #" required>
              {(() => {
                // When the prefix dropdown is in use, the number is assigned
                // by the server for the selected prefix — make it readonly
                // so the admin can't drift the auto-increment by accident.
                const lockNumber = Array.isArray(prefixes) && prefixes.length > 0
                                && onPrefixChange !== undefined;
                return (
                  <input className={s.input} type="text"
                         value={value?.studentNumber || ""}
                         onChange={e => update({ studentNumber: e.target.value })}
                         placeholder="S-2026-0001"
                         readOnly={lockNumber}
                         tabIndex={lockNumber ? -1 : undefined}
                         title={lockNumber ? "Auto-incremented from the server for the selected prefix" : ""}
                         style={lockNumber ? { background: "#f5f0e4", cursor: "not-allowed" } : undefined}
                         required disabled={disabled} />
                );
              })()}
            </Field>
            {showStatus && (
              <Field label="Status">
                <select className={s.input} value={value?.status || "PENDING"}
                        onChange={e => update({ status: e.target.value })}
                        disabled={disabled}>
                  <option value="PENDING">Pending (not enrolled yet)</option>
                  <option value="ENROLLED">Enrolled</option>
                  <option value="GRADUATED">Graduated</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </Field>
            )}
          </div>

          <div className={s.row}>
            <Field label="Last name" required>
              <input className={s.input} type="text"
                     value={value?.lastName || ""}
                     onChange={e => update({ lastName: e.target.value })}
                     required disabled={disabled} />
            </Field>
            <Field label="First name" required>
              <input className={s.input} type="text"
                     value={value?.firstName || ""}
                     onChange={e => update({ firstName: e.target.value })}
                     required disabled={disabled} />
            </Field>
            <Field label="Middle name">
              <input className={s.input} type="text"
                     value={value?.middleName || ""}
                     onChange={e => update({ middleName: e.target.value })}
                     disabled={disabled} />
            </Field>
          </div>

          <div className={s.rowBirthdate}>
            <Field label="Birthdate">
              <DatePicker value={value?.birthdate || ""}
                          onChange={v => update({ birthdate: v })}
                          placeholder="MM/DD/YYYY" />
            </Field>
            <Field label="Sex">
              <select className={s.input} value={value?.sex || ""}
                      onChange={e => update({ sex: e.target.value })}
                      disabled={disabled}>
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </Field>
            <Field label="Contact">
              <input className={s.input} type="text"
                     value={value?.contact || ""}
                     onChange={e => update({ contact: e.target.value })}
                     placeholder="Mobile / email"
                     disabled={disabled} />
            </Field>
          </div>

          <Field label="Address" wide>
            <textarea className={`${s.input} ${s.textarea}`}
                      rows={2}
                      value={value?.address || ""}
                      onChange={e => update({ address: e.target.value })}
                      disabled={disabled} />
          </Field>

          {showSectionPicker && (
            <Field label="Section">
              <SectionPicker
                value={value?.currentSectionId ? Number(value.currentSectionId) : null}
                onChange={id => update({ currentSectionId: id == null ? "" : id })}
                sections={sections}
                gradeLevels={gradeLevels}
              />
            </Field>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, wide, children }) {
  return (
    <div className={`${s.field} ${wide ? s.fieldWide : ""}`}>
      <label className={s.label}>
        {label}{required && <span className={s.req}> *</span>}
      </label>
      {children}
    </div>
  );
}

const PREFIX_STORAGE_KEY   = "studentNumberPrefix";    // legacy single value
const PREFIXES_STORAGE_KEY = "studentNumberPrefixes";  // new multi-prefix list

/**
 * Derive the default student-number prefix from the active School Year,
 * e.g. an active SY labelled "2026-2027" yields "S-2026-". Falls back to
 * the current calendar year if no active SY is set. Admins can still type
 * over the pre-filled number in the form if they need a different format.
 */
export function defaultStudentPrefix(schoolYears) {
  const active = (schoolYears || []).find(y => y.active);
  let year = null;
  if (active?.label) {
    const m = String(active.label).match(/\b(\d{4})\b/);
    if (m) year = m[1];
  }
  if (!year) year = String(new Date().getFullYear());
  return `S-${year}-`;
}

/** Read the admin's saved prefix (if any), else fall back to the active-SY
 *  default. Both student-create entry points use this so the prefix sticks
 *  across sessions once the admin has customised it. */
export function loadStudentPrefix(schoolYears) {
  return loadStudentPrefixes(schoolYears)[0];
}

/** Persist the admin's chosen prefix for next time. Empty string clears it
 *  so the next session falls back to the SY-derived default. Also promotes
 *  the prefix to the top of the shared multi-prefix list. */
export function saveStudentPrefix(prefix) {
  try {
    if (prefix) localStorage.setItem(PREFIX_STORAGE_KEY, prefix);
    else localStorage.removeItem(PREFIX_STORAGE_KEY);
  } catch { /* ignore */ }
}

/**
 * Read the configured list of student-number prefixes. Settings lets
 * SuperAdmins add/remove prefixes; Admissions picks from this list in its
 * Student # dropdown. Falls back to [legacy single prefix] or [SY default]
 * when no list has been saved yet.
 */
export function loadStudentPrefixes(schoolYears) {
  try {
    const raw = localStorage.getItem(PREFIXES_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map(String).filter(Boolean);
      }
    }
    const legacy = localStorage.getItem(PREFIX_STORAGE_KEY);
    if (legacy) return [legacy];
  } catch { /* storage may be unavailable */ }
  return [defaultStudentPrefix(schoolYears)];
}

/** Persist the Settings-managed prefix list. Empty list clears storage. */
export function saveStudentPrefixes(prefixes) {
  try {
    const arr = (prefixes || []).map(String).map(s => s).filter(Boolean);
    if (arr.length > 0) {
      localStorage.setItem(PREFIXES_STORAGE_KEY, JSON.stringify(arr));
    } else {
      localStorage.removeItem(PREFIXES_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}

/** Ask the server for the next available student number for the given
 *  prefix. Returns the full suggested number (e.g. "S-2026-00042") or
 *  null if the server wasn't reachable — callers treat null as "keep the
 *  existing blank value, admin will type one". */
export async function fetchNextStudentNumber(API_BASE, prefix) {
  try {
    const res = await fetch(`${API_BASE}/api/students/next-number?prefix=${encodeURIComponent(prefix)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data?.next || null;
  } catch { return null; }
}

/**
 * Upload a pending photo File to the student, if any. Returns the updated
 * student row (with photoUrl) or the original if no file was queued.
 * Centralised so all three entry points share the same post-save flow.
 */
export async function uploadStudentPhotoIfPending(API_BASE, authHeaders, studentId, file) {
  if (!file) return null;
  const fd = new FormData();
  fd.append("file", file);
  // Strip Content-Type — FormData sets the multipart boundary itself.
  const headers = { ...(typeof authHeaders === "function" ? authHeaders() : authHeaders) };
  delete headers["Content-Type"];
  const res = await fetch(`${API_BASE}/api/students/${studentId}/photo`, {
    method: "POST",
    headers,
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, `Photo upload failed (${res.status})`));
  }
  return res.json();
}
